import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { MapContainer, TileLayer, Marker, Popup, Polyline, CircleMarker, useMap } from 'react-leaflet';
import * as XLSX from 'xlsx';
import apiClient from '../../api/client';

const TZ = 'America/Mexico_City';
const PAGE_SIZE = 20;
const EXPORT_PAGE_SIZE = 500;
const EXPORT_MAX_PAGES = 10;

const COLUMNAS = [
    { key: 'ot', label: 'OT' },
    { key: 'origen', label: 'Origen' },
    { key: 'destino', label: 'Destino' },
    { key: 'base', label: 'Base' },
    { key: 'km', label: 'Km', className: 'text-right' },
    { key: 'fecha', label: 'Fecha de traslado' },
    { key: 'estatus', label: 'Estatus' },
];

const fmtFecha = (v) => (v ? new Date(v).toLocaleString('es-MX', { timeZone: TZ }) : '-');

const estatusTexto = (o) => (o.finalizada ? 'Finalizada' : o.iniciada ? 'En curso' : 'Pendiente');

// Descarta coordenadas basura: no numéricas, fuera de rango o cercanas a (0,0)
const toPoint = (lat, lon) => {
    const la = parseFloat(lat);
    const lo = parseFloat(lon);
    if (!Number.isFinite(la) || !Number.isFinite(lo)) return null;
    if (Math.abs(la) > 90 || Math.abs(lo) > 180) return null;
    if (Math.abs(la) < 1 && Math.abs(lo) < 1) return null;
    return [la, lo];
};

const FitBounds = ({ points }) => {
    const map = useMap();
    useEffect(() => {
        // El modal anima su apertura, así que se recalcula el tamaño antes de ajustar la vista
        const timer = setTimeout(() => {
            map.invalidateSize();
            if (points.length > 0) {
                map.fitBounds(points, { padding: [30, 30], animate: false });
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [map, points]);
    return null;
};

/**
 * Listado de OTs de un operador (filtro por fechas, orden y paginación en servidor),
 * exportación a Excel, acceso a casetas y mapa emergente del recorrido (geo_op).
 */
const OperadorOTsModal = ({ show, onHide, matricula, nombre, onVerCasetas }) => {
    const [ots, setOts] = useState([]);
    const [hayMas, setHayMas] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingMas, setLoadingMas] = useState(false);
    const [exportando, setExportando] = useState(false);
    const [error, setError] = useState('');
    const [fechas, setFechas] = useState({ desde: '', hasta: '' });
    const [filtros, setFiltros] = useState({ desde: '', hasta: '' });
    const [orden, setOrden] = useState({ key: 'fecha', dir: 'desc' });
    const [copiada, setCopiada] = useState('');
    const [mapa, setMapa] = useState({ show: false, ot: null, puntos: [], loading: false, error: '' });
    const [puntoSel, setPuntoSel] = useState(null);

    const buildParams = useCallback((extra) => ({
        orden: orden.key,
        dir: orden.dir,
        ...(filtros.desde && { desde: filtros.desde }),
        ...(filtros.hasta && { hasta: filtros.hasta }),
        ...extra,
    }), [orden, filtros]);

    useEffect(() => {
        if (!show) {
            setFechas({ desde: '', hasta: '' });
            setFiltros({ desde: '', hasta: '' });
            setOrden({ key: 'fecha', dir: 'desc' });
        }
    }, [show]);

    useEffect(() => {
        if (!show || !matricula) return;
        let cancelado = false;
        setLoading(true);
        setError('');
        apiClient.get(`/api/tracking/operadores/${matricula}/ots`, { params: buildParams({ limite: PAGE_SIZE, offset: 0 }) })
            .then((res) => {
                if (cancelado) return;
                setOts(res.data.ots || []);
                setHayMas(!!res.data.hayMas);
                setLoading(false);
            })
            .catch(() => {
                if (cancelado) return;
                setOts([]);
                setHayMas(false);
                setError('No se pudo cargar el listado de OTs del operador.');
                setLoading(false);
            });
        return () => { cancelado = true; };
    }, [show, matricula, buildParams]);

    const cargarMas = async () => {
        setLoadingMas(true);
        try {
            const res = await apiClient.get(`/api/tracking/operadores/${matricula}/ots`, {
                params: buildParams({ limite: PAGE_SIZE, offset: ots.length }),
            });
            setOts((prev) => [...prev, ...(res.data.ots || [])]);
            setHayMas(!!res.data.hayMas);
        } catch (err) {
            setError('No se pudieron cargar más OTs.');
        } finally {
            setLoadingMas(false);
        }
    };

    const aplicarFiltros = () => {
        if (fechas.desde && fechas.hasta && fechas.desde > fechas.hasta) {
            setError('La fecha "Desde" no puede ser mayor que "Hasta".');
            return;
        }
        setFiltros({ ...fechas });
    };

    const limpiarFiltros = () => {
        setFechas({ desde: '', hasta: '' });
        setFiltros({ desde: '', hasta: '' });
    };

    const ordenarPor = (key) => {
        setOrden((prev) => (prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
    };

    const copiarOT = (ot) => {
        navigator.clipboard.writeText(String(ot))
            .then(() => {
                setCopiada(String(ot));
                setTimeout(() => setCopiada(''), 1500);
            })
            .catch((err) => console.error('Error al copiar la OT:', err));
    };

    const exportarExcel = async () => {
        setExportando(true);
        try {
            let todas = [];
            for (let i = 0; i < EXPORT_MAX_PAGES; i++) {
                const res = await apiClient.get(`/api/tracking/operadores/${matricula}/ots`, {
                    params: buildParams({ limite: EXPORT_PAGE_SIZE, offset: todas.length }),
                });
                todas = todas.concat(res.data.ots || []);
                if (!res.data.hayMas) break;
            }
            const filas = todas.map((o) => ({
                OT: o.ot,
                'Cliente origen': o.clienteOrigen || '',
                Origen: o.origen || '',
                'Cliente destino': o.clienteDestino || '',
                Destino: o.destino || '',
                Base: o.base?.nombre || '',
                'Km planeados': o.km != null ? Number(o.km) : '',
                Asignada: o.asignada ? fmtFecha(o.asignada) : '',
                'En origen': o.enOrigen ? fmtFecha(o.enOrigen) : '',
                Iniciada: o.iniciada ? fmtFecha(o.iniciada) : '',
                'En destino': o.enDestino ? fmtFecha(o.enDestino) : '',
                Finalizada: o.finalizada ? fmtFecha(o.finalizada) : '',
                Estatus: estatusTexto(o),
            }));
            const ws = XLSX.utils.json_to_sheet(filas);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'OTs');
            XLSX.writeFile(wb, `OTs_Operador_${matricula}_${new Date().toISOString().slice(0, 10)}.xlsx`);
        } catch (err) {
            console.error('Error al exportar OTs:', err);
            setError('No se pudo exportar el listado a Excel.');
        } finally {
            setExportando(false);
        }
    };

    const verRecorrido = async (o) => {
        setPuntoSel(null);
        setMapa({ show: true, ot: o, puntos: [], loading: true, error: '' });
        try {
            const res = await apiClient.get(`/api/tracking/ot/${o.ot}`);
            const puntos = (res.data?.tracking || []).filter((p) => toPoint(p.latitud, p.longitud));
            setMapa({ show: true, ot: o, puntos, loading: false, error: '' });
        } catch (err) {
            setMapa({ show: true, ot: o, puntos: [], loading: false, error: 'No se pudo cargar el recorrido de la OT.' });
        }
    };

    const { coords, ptoOrigen, ptoDestino, todosLosPuntos } = useMemo(() => {
        const c = mapa.puntos.map((p) => [parseFloat(p.latitud), parseFloat(p.longitud)]);
        const po = mapa.ot ? toPoint(mapa.ot.latOrigen, mapa.ot.lonOrigen) : null;
        const pd = mapa.ot ? toPoint(mapa.ot.latDestino, mapa.ot.lonDestino) : null;
        return { coords: c, ptoOrigen: po, ptoDestino: pd, todosLosPuntos: [...c, ...(po ? [po] : []), ...(pd ? [pd] : [])] };
    }, [mapa.puntos, mapa.ot]);

    return (
        <>
            <Modal show={show} onHide={onHide} centered size="xl" scrollable>
                <Modal.Header closeButton>
                    <Modal.Title className="h5">
                        <i className="fas fa-list-alt mr-2 text-primary"></i>
                        OTs - {matricula} {nombre}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="form-inline mb-3">
                        <label className="mr-2 small font-weight-bold">Desde</label>
                        <input type="date" className="form-control form-control-sm mr-3" value={fechas.desde}
                            onChange={(e) => setFechas((f) => ({ ...f, desde: e.target.value }))} />
                        <label className="mr-2 small font-weight-bold">Hasta</label>
                        <input type="date" className="form-control form-control-sm mr-3" value={fechas.hasta}
                            onChange={(e) => setFechas((f) => ({ ...f, hasta: e.target.value }))} />
                        <button className="btn btn-sm btn-primary mr-2" onClick={aplicarFiltros}>
                            <i className="fas fa-filter mr-1"></i>Filtrar
                        </button>
                        <button className="btn btn-sm btn-outline-secondary mr-2" onClick={limpiarFiltros}>Limpiar</button>
                        <button className="btn btn-sm btn-success ml-auto" onClick={exportarExcel} disabled={exportando || loading || ots.length === 0}>
                            <i className={`fas ${exportando ? 'fa-spinner fa-spin' : 'fa-file-excel'} mr-1`}></i>Exportar a Excel
                        </button>
                    </div>

                    {error && <div className="alert alert-danger py-2">{error}</div>}

                    {loading ? (
                        <div className="text-center py-4"><div className="spinner-border text-primary" role="status"></div></div>
                    ) : ots.length === 0 ? (
                        !error && <div className="text-center text-muted py-4">No hay OTs para los filtros seleccionados.</div>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <table className="table table-sm table-bordered table-striped small mb-0">
                                    <thead className="thead-light">
                                        <tr>
                                            {COLUMNAS.map((c) => (
                                                <th key={c.key} className={c.className || ''} style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                                                    onClick={() => ordenarPor(c.key)} title={`Ordenar por ${c.label}`}>
                                                    {c.label}{' '}
                                                    <i className={`fas ${orden.key === c.key ? (orden.dir === 'asc' ? 'fa-sort-up' : 'fa-sort-down') : 'fa-sort text-muted'}`}></i>
                                                </th>
                                            ))}
                                            <th className="text-center">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {ots.map((o) => (
                                            <tr key={o.ot}>
                                                <td className="align-middle">
                                                    <span
                                                        className={`badge ${copiada === String(o.ot) ? 'badge-success' : 'badge-primary'}`}
                                                        style={{ cursor: 'pointer', fontSize: '0.8rem' }}
                                                        title="Clic para copiar la OT"
                                                        onClick={() => copiarOT(o.ot)}
                                                    >
                                                        {copiada === String(o.ot) ? <><i className="fas fa-check mr-1"></i>Copiada</> : o.ot}
                                                    </span>
                                                </td>
                                                <td className="align-middle">
                                                    <strong>{o.origen || '-'}</strong>
                                                    <div className="text-muted">{o.clienteOrigen || '-'}</div>
                                                </td>
                                                <td className="align-middle">
                                                    <strong>{o.destino || '-'}</strong>
                                                    <div className="text-muted">{o.clienteDestino || '-'}</div>
                                                </td>
                                                <td className="align-middle">{o.base?.nombre || '-'}</td>
                                                <td className="align-middle text-right">{o.km ? Number(o.km).toLocaleString('es-MX', { maximumFractionDigits: 1 }) : '-'}</td>
                                                <td className="align-middle">
                                                    <div><strong>Inicio:</strong> {fmtFecha(o.iniciada || o.enOrigen || o.asignada)}</div>
                                                    <div><strong>Fin:</strong> {fmtFecha(o.finalizada)}</div>
                                                </td>
                                                <td className="align-middle">
                                                    <span className={`badge ${o.finalizada ? 'badge-success' : o.iniciada ? 'badge-warning' : 'badge-secondary'}`}>{estatusTexto(o)}</span>
                                                </td>
                                                <td className="align-middle text-center text-nowrap">
                                                    <button className="btn btn-sm btn-outline-info mr-1" title="Ver recorrido en el mapa" onClick={() => verRecorrido(o)}>
                                                        <i className="fas fa-map-marked-alt"></i>
                                                    </button>
                                                    <button className="btn btn-sm btn-outline-primary" title={o.idTipoRuta ? 'Ver casetas' : 'La OT no tiene ruta asignada'}
                                                        disabled={!o.idTipoRuta || !onVerCasetas}
                                                        onClick={() => onVerCasetas(o)}>
                                                        <i className="fas fa-road"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="text-center mt-3">
                                <span className="small text-muted mr-3">{ots.length} OT{ots.length === 1 ? '' : 's'} cargadas</span>
                                {hayMas && (
                                    <button className="btn btn-sm btn-outline-primary" onClick={cargarMas} disabled={loadingMas}>
                                        {loadingMas ? <><span className="spinner-border spinner-border-sm mr-1"></span>Cargando...</> : 'Cargar más'}
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" size="sm" onClick={onHide}>Cerrar</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={mapa.show} onHide={() => setMapa((m) => ({ ...m, show: false }))} centered size="lg" style={{ zIndex: 1300 }}>
                <Modal.Header closeButton>
                    <Modal.Title className="h6">
                        <i className="fas fa-route mr-2 text-info"></i>Recorrido de la OT {mapa.ot?.ot} ({mapa.puntos.length} puntos GPS)
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0" style={{ height: '60vh' }}>
                    {mapa.loading ? (
                        <div className="text-center py-5"><div className="spinner-border text-primary" role="status"></div></div>
                    ) : mapa.error ? (
                        <div className="alert alert-danger m-3">{mapa.error}</div>
                    ) : todosLosPuntos.length === 0 ? (
                        <div className="text-center text-muted py-5">
                            <i className="fas fa-exclamation-triangle fa-2x text-warning mb-2"></i>
                            <p>No hay ubicaciones registradas para esta OT.</p>
                        </div>
                    ) : (
                        <MapContainer center={todosLosPuntos[0]} zoom={9} preferCanvas style={{ height: '100%', width: '100%' }}>
                            <FitBounds points={todosLosPuntos} />
                            <TileLayer
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            />
                            {coords.length > 0 && (
                                <>
                                    <Polyline positions={coords} color="#2563eb" weight={3} opacity={0.7} />
                                    {mapa.puntos.map((p, i) => (
                                        <CircleMarker
                                            key={p.Id_geo ?? i}
                                            center={coords[i]}
                                            radius={4}
                                            pathOptions={{ color: '#fff', weight: 1, fillColor: '#2563eb', fillOpacity: 1 }}
                                            eventHandlers={{ click: () => setPuntoSel(p) }}
                                        />
                                    ))}
                                    {puntoSel && (
                                        <Popup position={[parseFloat(puntoSel.latitud), parseFloat(puntoSel.longitud)]} onClose={() => setPuntoSel(null)}>
                                            <div className="text-center">
                                                <i className="far fa-clock mr-1 text-primary"></i><strong>{fmtFecha(puntoSel.fecha)}</strong>
                                                {puntoSel.velocidad != null && <div className="small text-muted">Velocidad: {Number(puntoSel.velocidad).toFixed(0)} km/h</div>}
                                            </div>
                                        </Popup>
                                    )}
                                    <Marker position={coords[0]}><Popup>Primer punto GPS<br />{fmtFecha(mapa.puntos[0].fecha)}</Popup></Marker>
                                    <Marker position={coords[coords.length - 1]}><Popup>Última ubicación<br />{fmtFecha(mapa.puntos[mapa.puntos.length - 1].fecha)}</Popup></Marker>
                                </>
                            )}
                            {ptoOrigen && (
                                <CircleMarker center={ptoOrigen} radius={10} pathOptions={{ color: '#155724', fillColor: '#28a745', fillOpacity: 0.85 }}>
                                    <Popup><strong>Origen</strong><br />{mapa.ot.origen}<br />{mapa.ot.clienteOrigen}</Popup>
                                </CircleMarker>
                            )}
                            {ptoDestino && (
                                <CircleMarker center={ptoDestino} radius={10} pathOptions={{ color: '#721c24', fillColor: '#dc3545', fillOpacity: 0.85 }}>
                                    <Popup><strong>Destino</strong><br />{mapa.ot.destino}<br />{mapa.ot.clienteDestino}</Popup>
                                </CircleMarker>
                            )}
                        </MapContainer>
                    )}
                </Modal.Body>
                <Modal.Footer className="py-2 small">
                    <span className="mr-3"><i className="fas fa-circle text-success mr-1"></i>Origen</span>
                    <span><i className="fas fa-circle text-danger mr-1"></i>Destino</span>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default OperadorOTsModal;
